import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { functions, db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  Play,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Clock,
  Plus,
  Trash2,
  Gauge,
  LogOut,
  Layers,
} from 'lucide-react';
import CameraCaptureModal from './CameraCaptureModal';
import { formatTempoEstimado } from '../../utils/tempoUnidade';
import { uploadApontamentoFoto, formatarCronometro, ordenarPorLote, mensagemErroFirebase } from './kioskUtils';
import { formatQuantidade, formatQuantidadeComUnidade } from '../../utils/numberFormat';
import {
  ParametrosEtapaForm,
  FotosEtapaCapture,
  construirParametrosPayload,
  construirFotosEtapaPayload,
  fotosEtapaFaltando,
  obrigatoriosFaltando,
  PesoFinalEtapaField,
  parsePesoFinal,
  EmbalagensEtapaField,
} from './ParametrosEtapa';

function fmtDataBr(ymd) {
  const s = String(ymd || '').slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Etapa parametrizada atual do carrinho (a partir da rota). */
function etapaDoCarrinho(c) {
  const rota = c?.rota_setores || [];
  return rota[Number(c?.etapa_atual_ordem || 0)] || {};
}

/** View da etapa reduzida ao ciclo atual (para formulário de parâmetros do ciclo). */
function etapaCicloAtual(c) {
  const etapa = etapaDoCarrinho(c);
  if (!etapa.ciclos_ativo || c.ciclos_completos) return null;
  const num = Number(c.ciclo_atual || 1);
  const ciclo = (etapa.ciclos || [])[num - 1];
  if (!ciclo) return null;
  return {
    ...etapa,
    parametros: ciclo.parametros || [],
    validacoes: [],
    fotos_etapa: ciclo.fotos_ciclo || [],
    peso_final_ativo: false,
    _cicloNum: num,
    _totalCiclos: (etapa.ciclos || []).length,
  };
}

function precisaRegistrarCiclo(c) {
  const etapa = etapaDoCarrinho(c);
  return !!(etapa.ciclos_ativo && !c.ciclos_completos && (etapa.ciclos || []).length > 0);
}

function normCard(v) {
  const s = String(v || '').toLowerCase().replace(/\s/g, '');
  return ['1-1', '1-n', 'n-1', 'n-m'].includes(s) ? s : '1-1';
}
/** Cardinalidade da etapa atual do carrinho. */
function cardDoCarrinho(c) {
  return normCard(etapaDoCarrinho(c).cardinalidade);
}
/** Entrada agrupada (n-1, n-m): processa vários carrinhos juntos no equipamento. */
function entradaAgrupada(c) {
  return cardDoCarrinho(c).startsWith('n');
}
/** Saída múltipla (1-n, n-m): pode gerar vários carrinhos. */
function saidaPermiteVarios(c) {
  const card = cardDoCarrinho(c);
  return card.endsWith('-n') || card.endsWith('-m');
}

const STATUS_ATIVO = ['disponivel', 'em_processamento'];

const KioskSetorPage = () => {
  const { setorId } = useParams();
  const navigate = useNavigate();
  const { getEmpresaId, logout } = useAuth();
  const empresaId = getEmpresaId();

  const sair = async () => {
    await logout();
    navigate('/app/login', { replace: true });
  };

  const [setor, setSetor] = useState(null);
  const [operadores, setOperadores] = useState([]);
  const [operadorUid, setOperadorUid] = useState('');
  const [carrinhos, setCarrinhos] = useState([]);
  const [cargas, setCargas] = useState([]);
  const [fornos, setFornos] = useState([]);
  const [ordensDia, setOrdensDia] = useState([]);
  const [mostrarPlano, setMostrarPlano] = useState(true);
  const [erro, setErro] = useState('');
  const [agora, setAgora] = useState(Date.now());

  // Modais
  const [camera, setCamera] = useState(null); // { titulo, run: async(fotoUrl)=>{} }
  const [cameraBusy, setCameraBusy] = useState(false);
  const [finalizar, setFinalizar] = useState(null); // carrinho
  const [perda, setPerda] = useState(null); // carrinho
  const [params, setParams] = useState(null); // carrinho
  const [cicloModal, setCicloModal] = useState(null); // carrinho
  const [cargaNova, setCargaNova] = useState(false);
  const [finalizarCarga, setFinalizarCarga] = useState(null); // carga

  const ehForno = useMemo(() => {
    if (setor?.nome && /coz|forno/i.test(setor.nome)) return true;
    return carrinhos.some((c) => c.setor_atual_id === setorId && c.is_forno_atual);
  }, [setor, carrinhos, setorId]);

  // Carrega setor + operadores + fornos
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const [rSetor, rUsers, rEq] = await Promise.all([
          httpsCallable(functions, 'getSetor')({ empresa_id: empresaId, id: setorId }),
          httpsCallable(functions, 'listUsuariosProducaoSetor')({ empresa_id: empresaId }),
          httpsCallable(functions, 'listEquipamentos')({ empresa_id: empresaId }),
        ]);
        if (!ativo) return;
        const setorData = rSetor.data?.data || null;
        setSetor(setorData);
        const usuarioIds = setorData?.usuario_ids || [];
        setOperadores((rUsers.data?.data?.items || []).filter((u) => usuarioIds.includes(u.id)));
        setFornos((rEq.data?.data?.items || []).filter((e) => e.setor_id === setorId && e.status !== 'baixado'));
      } catch (e) {
        if (ativo) setErro(e.message || 'Erro ao carregar o setor.');
      }
    })();
    return () => {
      ativo = false;
    };
  }, [empresaId, setorId]);

  // Tempo real: carrinhos da empresa
  useEffect(() => {
    if (!empresaId || empresaId === 'default') return undefined;
    const q = query(collection(db, 'Carrinhos_Producao'), where('empresa_id', '==', empresaId));
    const unsub = onSnapshot(
      q,
      (snap) => setCarrinhos(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => setErro(e.message || 'Erro no tempo real.')
    );
    return () => unsub();
  }, [empresaId]);

  // Tempo real: cargas de forno abertas do setor
  useEffect(() => {
    if (!empresaId || empresaId === 'default') return undefined;
    const q = query(
      collection(db, 'Cargas_Forno'),
      where('empresa_id', '==', empresaId),
      where('setor_id', '==', setorId),
      where('status', '==', 'cozinhando')
    );
    const unsub = onSnapshot(
      q,
      (snap) => setCargas(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return () => unsub();
  }, [empresaId, setorId]);

  // Cronômetro
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Planejamento do dia (todas as ordens ativas do dia), atualizado periodicamente
  useEffect(() => {
    if (!empresaId || empresaId === 'default') return undefined;
    let ativo = true;
    const carregar = async () => {
      try {
        const r = await httpsCallable(functions, 'listOrdensFluxoSetorial')({ empresa_id: empresaId });
        if (!ativo) return;
        const items = r.data?.data?.items || [];
        setOrdensDia(items);
      } catch {
        /* silencioso: painel informativo */
      }
    };
    carregar();
    const t = setInterval(carregar, 60000);
    return () => {
      ativo = false;
      clearInterval(t);
    };
  }, [empresaId]);

  // Agrupa carrinhos para este setor
  const grupos = useMemo(() => {
    const disponiveisIndividual = [];
    const disponiveisGrupo = [];
    const andamento = [];
    const processados = [];
    const aCaminho = [];
    const emCarga = new Set();
    cargas.forEach((cg) => (cg.carrinho_ids_entrada || []).forEach((id) => emCarga.add(id)));

    for (const c of carrinhos) {
      const rota = c.rota_setores || [];
      const idx = rota.findIndex((r) => r.setor_id === setorId);
      if (idx < 0) continue;
      const etapaAtual = Number(c.etapa_atual_ordem || 0);
      if (etapaAtual === idx && c.setor_atual_id === setorId) {
        if (c.status === 'disponivel') {
          if (entradaAgrupada(c)) disponiveisGrupo.push(c);
          else disponiveisIndividual.push(c);
        } else if (c.status === 'em_processamento' && !emCarga.has(c.id)) andamento.push(c);
        else if (['processado', 'concluido', 'enviado'].includes(c.status)) processados.push(c);
      } else if (etapaAtual < idx && STATUS_ATIVO.includes(c.status)) {
        aCaminho.push(c);
      }
    }
    disponiveisIndividual.sort(ordenarPorLote);
    disponiveisGrupo.sort(ordenarPorLote);
    andamento.sort(ordenarPorLote);
    processados.sort(ordenarPorLote);
    aCaminho.sort(ordenarPorLote);
    return { disponiveisIndividual, disponiveisGrupo, andamento, processados, aCaminho };
  }, [carrinhos, cargas, setorId]);

  const temAgrupamento = grupos.disponiveisGrupo.length > 0 || cargas.length > 0;

  // Planejamento do dia com status relativo a ESTE setor
  const planoDia = useMemo(() => {
    return ordensDia.map((o) => {
      const cs = carrinhos.filter((c) => c.ordem_id === o.id);
      let disponiveis = 0;
      let processando = 0;
      let concluidos = 0;
      let aCaminho = 0;
      for (const c of cs) {
        const rota = c.rota_setores || [];
        const idx = rota.findIndex((r) => r.setor_id === setorId);
        if (idx < 0) continue;
        const etapaAtual = Number(c.etapa_atual_ordem || 0);
        if (etapaAtual === idx && c.setor_atual_id === setorId) {
          if (c.status === 'disponivel') disponiveis += 1;
          else if (c.status === 'em_processamento') processando += 1;
          else if (['processado', 'concluido', 'enviado'].includes(c.status)) concluidos += 1;
        } else if (etapaAtual > idx) {
          concluidos += 1;
        } else if (etapaAtual < idx && STATUS_ATIVO.includes(c.status)) {
          aCaminho += 1;
        }
      }
      return {
        id: o.id,
        lote: o.lote_fabricacao,
        data: o.data_planejada || '',
        produto: o.produto_nome,
        status: o.status,
        carrinhos: cs.length,
        disponiveis,
        processando,
        concluidos,
        aCaminho,
      };
    });
  }, [ordensDia, carrinhos, setorId]);

  const dataPorOrdem = useMemo(() => {
    const m = new Map();
    for (const o of ordensDia) m.set(o.id, o.data_planejada || '');
    return m;
  }, [ordensDia]);

  const exigeOperador = useCallback(() => {
    if (!operadorUid) {
      setErro('Selecione o operador no topo antes de registrar.');
      return false;
    }
    setErro('');
    return true;
  }, [operadorUid]);

  // Abre a câmera e executa a ação ao capturar a foto
  const comFoto = useCallback(
    (titulo, run) => {
      if (!exigeOperador()) return;
      setErro('');
      setCamera({ titulo, run });
    },
    [exigeOperador]
  );

  const onCapturarFoto = useCallback(
    async (blob) => {
      if (!camera) return;
      setCameraBusy(true);
      setErro('');
      try {
        const fotoUrl = await uploadApontamentoFoto(blob, { empresaId, setorId });
        await camera.run(fotoUrl);
        setCamera(null);
        setFinalizar(null);
        setPerda(null);
        setParams(null);
        setCargaNova(false);
        setFinalizarCarga(null);
        setCicloModal(null);
      } catch (e) {
        console.error('Falha ao registrar apontamento com foto:', e);
        setErro(mensagemErroFirebase(e, 'Erro ao registrar o apontamento.'));
      } finally {
        setCameraBusy(false);
      }
    },
    [camera, empresaId, setorId]
  );

  const call = (name, data) => httpsCallable(functions, name)({ empresa_id: empresaId, ...data });

  const iniciar = (c) =>
    comFoto(`Iniciar ${c.lote_fabricacao}`, (foto) =>
      call('iniciarProcessamentoCarrinho', { carrinho_id: c.id, operador_uid: operadorUid, foto_url: foto })
    );

  const abrirAmostra = (ordemId, lote) => {
    if (!exigeOperador()) return;
    const base = carrinhos.find(
      (c) => c.ordem_id === ordemId && (c.rota_setores || []).some((r) => r.setor_id === setorId)
    );
    if (!base) {
      setErro('Este lote ainda não tem a etapa deste setor na rota.');
      return;
    }
    const idx = (base.rota_setores || []).findIndex((r) => r.setor_id === setorId);
    setParams({
      ...base,
      lote_fabricacao: lote || base.lote_fabricacao,
      etapa_atual_ordem: idx >= 0 ? idx : base.etapa_atual_ordem,
      _amostraSetorId: setorId,
    });
  };

  const nomeOperador = operadores.find((o) => o.id === operadorUid)?.nome || '';

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Cabeçalho fixo */}
      <header className="sticky top-0 z-20 flex flex-wrap items-center gap-3 bg-gray-900 px-4 py-3 text-white">
        <button onClick={() => navigate('/app/trabalho')} className="rounded-lg p-2 hover:bg-white/10">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          {temAgrupamento ? <Layers className="h-6 w-6 text-orange-400" /> : <Gauge className="h-6 w-6 text-emerald-400" />}
          <div>
            <h1 className="text-xl font-bold leading-tight">{setor?.nome || 'Setor'}</h1>
            <p className="text-xs text-gray-300">Chão de fábrica · tempo real</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-gray-300">Operador:</span>
          <select
            value={operadorUid}
            onChange={(e) => setOperadorUid(e.target.value)}
            className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-900"
          >
            <option value="">Selecione…</option>
            {operadores.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nome}
              </option>
            ))}
          </select>
          <button
            onClick={sair}
            className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/20"
            title="Sair"
          >
            <LogOut className="h-4 w-4" /> Sair
          </button>
        </div>
      </header>

      {erro && (
        <div className="mx-4 mt-3 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700">
          {erro}
        </div>
      )}

      <main className="mx-auto max-w-5xl space-y-6 p-4">
        {/* Planejamento do dia (visível em todos os setores) */}
        <section className="rounded-xl border bg-white">
          <button
            onClick={() => setMostrarPlano((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-3"
          >
            <span className="text-sm font-semibold uppercase tracking-wide text-gray-600">
              Produção do dia ({planoDia.length} lote{planoDia.length === 1 ? '' : 's'})
            </span>
            <span className="text-xs text-gray-400">{mostrarPlano ? 'ocultar' : 'mostrar'}</span>
          </button>
          {mostrarPlano && (
            <div className="border-t px-2 pb-2">
              {planoDia.length === 0 ? (
                <p className="px-2 py-3 text-sm text-gray-400">Nenhum lote em aberto.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-gray-400">
                        <th className="px-2 py-2">Data</th>
                        <th className="px-2 py-2">Lote</th>
                        <th className="px-2 py-2">Produto</th>
                        <th className="px-2 py-2 text-center">Disponíveis aqui</th>
                        <th className="px-2 py-2 text-center">Em processo</th>
                        <th className="px-2 py-2 text-center">Concluídos aqui</th>
                        <th className="px-2 py-2 text-center">A caminho</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {planoDia.map((p) => (
                        <tr key={p.id} className="border-t">
                          <td className="px-2 py-2 whitespace-nowrap text-gray-700">{fmtDataBr(p.data)}</td>
                          <td className="px-2 py-2 font-semibold text-gray-900">{p.lote}</td>
                          <td className="px-2 py-2 text-gray-600">{p.produto}</td>
                          <td className="px-2 py-2 text-center">
                            {p.disponiveis > 0 ? (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700">
                                {p.disponiveis}
                              </span>
                            ) : (
                              <span className="text-gray-300">0</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center text-blue-700">{p.processando || ''}</td>
                          <td className="px-2 py-2 text-center text-gray-500">{p.concluidos || ''}</td>
                          <td className="px-2 py-2 text-center text-gray-400">{p.aCaminho || ''}</td>
                          <td className="px-2 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => abrirAmostra(p.id, p.lote)}
                              className="whitespace-nowrap rounded-lg border border-blue-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                            >
                              Parâmetros
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Agrupamento (etapas n-1 / n-m): processar vários carrinhos juntos no equipamento */}
        {temAgrupamento && (
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Processamento em equipamento</h2>
              <button
                onClick={() => exigeOperador() && setCargaNova(true)}
                className="flex items-center gap-1 rounded-lg bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
                disabled={grupos.disponiveisGrupo.length === 0}
              >
                <Plus className="h-4 w-4" /> Pôr carrinhos no equipamento
              </button>
            </div>
            {cargas.length === 0 ? (
              <p className="rounded-lg bg-white px-4 py-3 text-sm text-gray-400">Nenhum grupo em andamento.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {cargas.map((cg) => (
                  <div key={cg.id} className="rounded-xl border bg-white p-4">
                    <div className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-orange-500" />
                      <span className="font-semibold text-gray-900">{cg.equipamento_nome}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      {(cg.carrinho_ids_entrada || []).length} carrinho(s) no equipamento
                    </p>
                    <button
                      onClick={() => exigeOperador() && setFinalizarCarga(cg)}
                      className="mt-3 w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                      Finalizar processamento
                    </button>
                  </div>
                ))}
              </div>
            )}
            {grupos.disponiveisGrupo.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-gray-500">Aguardando agrupamento:</p>
                {grupos.disponiveisGrupo.map((c) => (
                  <CarrinhoLinha key={c.id} c={c} cor="emerald" data={fmtDataBr(dataPorOrdem.get(c.ordem_id))}>
                    <span className="text-xs text-gray-400">Selecione em “Pôr carrinhos no equipamento”.</span>
                  </CarrinhoLinha>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Disponíveis individuais (topo) */}
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-emerald-700">
            Disponíveis para iniciar ({grupos.disponiveisIndividual.length})
          </h2>
          <div className="space-y-2">
            {grupos.disponiveisIndividual.length === 0 && (
              <p className="rounded-lg bg-white px-4 py-3 text-sm text-gray-400">Nada disponível no momento.</p>
            )}
            {grupos.disponiveisIndividual.map((c) => (
              <CarrinhoLinha key={c.id} c={c} cor="emerald" data={fmtDataBr(dataPorOrdem.get(c.ordem_id))}>
                <button
                  onClick={() => iniciar(c)}
                  className="flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  <Play className="h-4 w-4" /> Iniciar
                </button>
              </CarrinhoLinha>
            ))}
          </div>
        </section>

        {/* Em andamento */}
        {grupos.andamento.length > 0 && (
          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-blue-700">
              Em processamento ({grupos.andamento.length})
            </h2>
            <div className="space-y-2">
              {grupos.andamento.map((c) => (
                <CarrinhoLinha
                  key={c.id}
                  c={c}
                  cor="blue"
                  data={fmtDataBr(dataPorOrdem.get(c.ordem_id))}
                  timer={formatarCronometro(c.tempo_inicio, agora)}
                >
                  <button
                    onClick={() => exigeOperador() && setParams(c)}
                    className="flex items-center gap-1 rounded-lg border border-blue-300 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                  >
                    <Gauge className="h-4 w-4" /> Parâmetros
                  </button>
                  {precisaRegistrarCiclo(c) ? (
                    <button
                      onClick={() => exigeOperador() && setCicloModal(c)}
                      className="flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                    >
                      <CheckCircle2 className="h-4 w-4" /> Ciclo {c.ciclo_atual || 1}
                    </button>
                  ) : (
                    <button
                      onClick={() => exigeOperador() && setFinalizar(c)}
                      className="flex items-center gap-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                    >
                      <CheckCircle2 className="h-4 w-4" /> Finalizar
                    </button>
                  )}
                  <button
                    onClick={() => exigeOperador() && setPerda(c)}
                    className="rounded-lg border border-red-300 p-2 text-red-600 hover:bg-red-50"
                    title="Marcar perda"
                  >
                    <AlertTriangle className="h-4 w-4" />
                  </button>
                </CarrinhoLinha>
              ))}
            </div>
          </section>
        )}

        {/* A caminho (cinza) */}
        {grupos.aCaminho.length > 0 && (
          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-400">
              A caminho ({grupos.aCaminho.length})
            </h2>
            <div className="space-y-2 opacity-50">
              {grupos.aCaminho.map((c) => (
                <CarrinhoLinha
                  key={c.id}
                  c={c}
                  cor="gray"
                  data={fmtDataBr(dataPorOrdem.get(c.ordem_id))}
                  subtitulo={`No setor: ${c.setor_atual_nome || '—'}`}
                >
                  <span className="text-xs text-gray-400">Indisponível</span>
                </CarrinhoLinha>
              ))}
            </div>
          </section>
        )}

        {/* Processados (fim) */}
        {grupos.processados.length > 0 && (
          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Processados hoje ({grupos.processados.length})
            </h2>
            <div className="space-y-2">
              {grupos.processados.map((c) => (
                <CarrinhoLinha
                  key={c.id}
                  c={c}
                  cor="green"
                  data={fmtDataBr(dataPorOrdem.get(c.ordem_id))}
                  subtitulo={`Saída: ${formatQuantidadeComUnidade(c.quantidade_saida, c.unidade, '—')} · perda ${formatQuantidade(c.perda_etapa ?? 0)}`}
                >
                  <span className="flex items-center gap-1 text-sm text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" /> Concluído
                  </span>
                </CarrinhoLinha>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Modais */}
      {camera && (
        <CameraCaptureModal
          open
          title={`${camera.titulo} — ${nomeOperador}`}
          busy={cameraBusy}
          erroExterno={erro}
          onCapture={onCapturarFoto}
          onCancel={() => {
            if (cameraBusy) return;
            setCamera(null);
            setErro('');
          }}
        />
      )}

      {finalizar && (
        <FinalizarModal
          carrinho={finalizar}
          etapa={etapaDoCarrinho(finalizar)}
          permiteVarios={saidaPermiteVarios(finalizar)}
          empresaId={empresaId}
          setorId={setorId}
          onCancel={() => setFinalizar(null)}
          onConfirm={({ saidas, parametros, fotos_etapa, peso_final, embalagens_utilizadas }) =>
            comFoto(`Finalizar ${finalizar.lote_fabricacao}`, (foto) =>
              call('finalizarProcessamentoCarrinho', {
                carrinho_id: finalizar.id,
                operador_uid: operadorUid,
                foto_url: foto,
                saidas,
                parametros,
                fotos_etapa,
                peso_final,
                embalagens_utilizadas,
              })
            )
          }
        />
      )}

      {params && (
        <ParametrosModal
          carrinho={params}
          etapa={etapaDoCarrinho(params)}
          empresaId={empresaId}
          setorId={setorId}
          onCancel={() => setParams(null)}
          onConfirm={({ parametros, fotos_etapa, peso_final }) =>
            comFoto(`Parâmetros ${params.lote_fabricacao}`, (foto) =>
              call('registrarApontamentoParametros', {
                carrinho_id: params.id,
                operador_uid: operadorUid,
                foto_url: foto,
                parametros,
                fotos_etapa,
                peso_final,
                ...(params._amostraSetorId ? { setor_id: params._amostraSetorId } : {}),
              })
            )
          }
        />
      )}

      {cicloModal && (
        <CicloModal
          carrinho={cicloModal}
          etapa={etapaCicloAtual(cicloModal)}
          empresaId={empresaId}
          setorId={setorId}
          onCancel={() => setCicloModal(null)}
          onConfirm={({ parametros, fotos_ciclo }) =>
            comFoto(`Ciclo ${cicloModal.ciclo_atual || 1} · ${cicloModal.lote_fabricacao}`, (foto) =>
              call('registrarCicloCarrinho', {
                carrinho_id: cicloModal.id,
                operador_uid: operadorUid,
                foto_url: foto,
                parametros,
                fotos_ciclo: fotos_ciclo || [],
              }).then(() => setCicloModal(null))
            )
          }
        />
      )}

      {perda && (
        <PerdaModal
          carrinho={perda}
          onCancel={() => setPerda(null)}
          onConfirm={(motivo) =>
            comFoto(`Perda ${perda.lote_fabricacao}`, (foto) =>
              call('marcarCarrinhoPerda', {
                carrinho_id: perda.id,
                operador_uid: operadorUid,
                foto_url: foto,
                motivo,
              })
            )
          }
        />
      )}

      {cargaNova && (
        <NovaCargaModal
          disponiveis={grupos.disponiveisGrupo}
          fornos={fornos}
          onCancel={() => setCargaNova(false)}
          onConfirm={({ equipamento_id, carrinho_ids }) =>
            comFoto('Nova carga de forno', (foto) =>
              call('criarCargaForno', {
                setor_id: setorId,
                equipamento_id,
                carrinho_ids,
                operador_uid: operadorUid,
                foto_url: foto,
              })
            )
          }
        />
      )}

      {finalizarCarga && (
        <FinalizarCargaModal
          carga={finalizarCarga}
          carrinhos={carrinhos}
          empresaId={empresaId}
          setorId={setorId}
          onCancel={() => setFinalizarCarga(null)}
          onConfirm={({ saidas, parametros, fotos_etapa, peso_final, embalagens_utilizadas }) =>
            comFoto('Finalizar cozimento', (foto) =>
              call('finalizarCargaForno', {
                carga_forno_id: finalizarCarga.id,
                operador_uid: operadorUid,
                foto_url: foto,
                saidas,
                parametros,
                fotos_etapa,
                peso_final,
                embalagens_utilizadas,
              })
            )
          }
        />
      )}
    </div>
  );
};

// ---------- Subcomponentes ----------

function CarrinhoLinha({ c, cor, timer, subtitulo, data, children }) {
  const borda = {
    emerald: 'border-l-emerald-500',
    blue: 'border-l-blue-500',
    gray: 'border-l-gray-300',
    green: 'border-l-emerald-400',
  }[cor || 'gray'];
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border border-l-4 ${borda} bg-white px-4 py-3`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-gray-900">{c.lote_fabricacao}</span>
          {data ? <span className="text-sm font-medium text-gray-500">{data}</span> : null}
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {c.lote_carrinho}
          </span>
        </div>
        <p className="truncate text-sm text-gray-500">
          {c.produto_nome} · {formatQuantidadeComUnidade(c.quantidade, c.unidade, '—')}
          {subtitulo ? ` · ${subtitulo}` : ''}
        </p>
      </div>
      {timer && (
        <span className="flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-1 font-mono text-sm font-semibold text-blue-700">
          <Clock className="h-4 w-4" /> {timer}
        </span>
      )}
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

function ModalShell({ titulo, children, onCancel, onConfirm, confirmLabel, disabled }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="border-b px-5 py-4 text-lg font-semibold text-gray-900">{titulo}</div>
        <div className="space-y-3 px-5 py-4">{children}</div>
        <div className="flex justify-end gap-2 border-t px-5 py-4">
          <button onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={disabled}
            className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {confirmLabel || 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FinalizarModal({ carrinho, etapa, permiteVarios, empresaId, setorId, onCancel, onConfirm }) {
  const [saidas, setSaidas] = useState([{ quantidade: carrinho.quantidade ?? '' }]);
  const [qualidadeVals, setQualidadeVals] = useState({});
  const [producaoVals, setProducaoVals] = useState({});
  const [fotos, setFotos] = useState({});
  const [pesoFinal, setPesoFinal] = useState('');
  const [embalagensSel, setEmbalagensSel] = useState([]);
  const [aviso, setAviso] = useState('');
  const addSaida = () => setSaidas([...saidas, { quantidade: '' }]);
  const updSaida = (i, v) => setSaidas(saidas.map((s, idx) => (idx === i ? { quantidade: v } : s)));
  const delSaida = (i) => setSaidas(saidas.filter((_, idx) => idx !== i));

  const confirmar = () => {
    const faltam = obrigatoriosFaltando(etapa, qualidadeVals, pesoFinal);
    if (faltam.length) {
      setAviso(`Preencha os parâmetros obrigatórios: ${faltam.join(', ')}`);
      return;
    }
    const fotosFaltam = fotosEtapaFaltando(etapa, fotos);
    if (fotosFaltam.length) {
      setAviso(`Envie as fotos da etapa: ${fotosFaltam.join(', ')}`);
      return;
    }
    if ((etapa?.embalagens_permitidas || []).length > 0 && embalagensSel.length === 0) {
      setAviso('Selecione ao menos uma embalagem utilizada.');
      return;
    }
    onConfirm({
      saidas: saidas.map((s) => ({ quantidade: s.quantidade })),
      parametros: construirParametrosPayload(etapa, qualidadeVals, producaoVals),
      fotos_etapa: construirFotosEtapaPayload(etapa, fotos),
      peso_final: etapa?.peso_final_ativo ? parsePesoFinal(pesoFinal) : null,
      embalagens_utilizadas: embalagensSel,
    });
  };

  return (
    <ModalShell titulo={`Finalizar ${carrinho.lote_fabricacao}`} onCancel={onCancel} onConfirm={confirmar} confirmLabel="Tirar foto e finalizar">
      <p className="text-sm text-gray-500">
        Entrada: {formatQuantidadeComUnidade(carrinho.quantidade, carrinho.unidade, '—')}. Informe a(s) quantidade(s) de saída
        (1 linha por carrinho gerado).
      </p>
      <div className="space-y-2">
        {saidas.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Carrinho {i + 1}</span>
            <input
              type="number"
              value={s.quantidade}
              onChange={(e) => updSaida(i, e.target.value)}
              placeholder="Qtd saída"
              className="flex-1 rounded-lg border px-2 py-1.5 text-sm"
            />
            {saidas.length > 1 && (
              <button onClick={() => delSaida(i)} className="p-1 text-red-500">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        {permiteVarios && (
          <button onClick={addSaida} className="flex items-center gap-1 text-sm text-emerald-700">
            <Plus className="h-4 w-4" /> Dividir em mais carrinhos
          </button>
        )}
      </div>
      <ParametrosEtapaForm
        etapa={etapa}
        qualidadeVals={qualidadeVals}
        setQualidadeVals={setQualidadeVals}
        producaoVals={producaoVals}
        setProducaoVals={setProducaoVals}
      />
      <PesoFinalEtapaField
        etapa={etapa}
        valor={pesoFinal}
        setValor={setPesoFinal}
        unidade={carrinho.unidade}
      />
      <EmbalagensEtapaField etapa={etapa} selecionadas={embalagensSel} setSelecionadas={setEmbalagensSel} />
      <FotosEtapaCapture etapa={etapa} valores={fotos} setValores={setFotos} empresaId={empresaId} setorId={setorId} />
      {aviso && <p className="text-sm text-red-600">{aviso}</p>}
    </ModalShell>
  );
}

function ParametrosModal({ carrinho, etapa, empresaId, setorId, onCancel, onConfirm }) {
  const [qualidadeVals, setQualidadeVals] = useState({});
  const [producaoVals, setProducaoVals] = useState({});
  const [fotos, setFotos] = useState({});
  const [pesoFinal, setPesoFinal] = useState('');
  const [aviso, setAviso] = useState('');

  const confirmar = () => {
    const faltam = obrigatoriosFaltando(etapa, qualidadeVals, pesoFinal);
    if (faltam.length) {
      setAviso(`Preencha os parâmetros obrigatórios: ${faltam.join(', ')}`);
      return;
    }
    const fotosFaltam = fotosEtapaFaltando(etapa, fotos);
    if (fotosFaltam.length) {
      setAviso(`Envie as fotos da etapa: ${fotosFaltam.join(', ')}`);
      return;
    }
    onConfirm({
      parametros: construirParametrosPayload(etapa, qualidadeVals, producaoVals),
      fotos_etapa: construirFotosEtapaPayload(etapa, fotos),
      peso_final: etapa?.peso_final_ativo ? parsePesoFinal(pesoFinal) : null,
    });
  };

  return (
    <ModalShell titulo={`Parâmetros ${carrinho.lote_fabricacao}`} onCancel={onCancel} onConfirm={confirmar} confirmLabel="Tirar foto e registrar">
      {carrinho._amostraSetorId ? (
        <p className="text-sm text-gray-500">
          Amostra deste lote nesta etapa. O carrinho não precisa estar em processamento.
        </p>
      ) : null}
      <ParametrosEtapaForm
        etapa={etapa}
        qualidadeVals={qualidadeVals}
        setQualidadeVals={setQualidadeVals}
        producaoVals={producaoVals}
        setProducaoVals={setProducaoVals}
      />
      <PesoFinalEtapaField
        etapa={etapa}
        valor={pesoFinal}
        setValor={setPesoFinal}
        unidade={carrinho.unidade}
      />
      <FotosEtapaCapture etapa={etapa} valores={fotos} setValores={setFotos} empresaId={empresaId} setorId={setorId} />
      {aviso && <p className="text-sm text-red-600">{aviso}</p>}
    </ModalShell>
  );
}

function CicloModal({ carrinho, etapa, empresaId, setorId, onCancel, onConfirm }) {
  const [qualidadeVals, setQualidadeVals] = useState({});
  const [fotos, setFotos] = useState({});
  const [aviso, setAviso] = useState('');
  if (!etapa) {
    return (
      <ModalShell titulo="Ciclo" onCancel={onCancel} onConfirm={onCancel} confirmLabel="Fechar">
        <p className="text-sm text-red-600">Ciclo não encontrado nesta etapa.</p>
      </ModalShell>
    );
  }
  const confirmar = () => {
    const faltam = obrigatoriosFaltando(etapa, qualidadeVals, '');
    if (faltam.length) {
      setAviso(`Preencha: ${faltam.join(', ')}`);
      return;
    }
    const fotosFaltam = fotosEtapaFaltando(etapa, fotos);
    if (fotosFaltam.length) {
      setAviso(`Envie as fotos: ${fotosFaltam.join(', ')}`);
      return;
    }
    onConfirm({
      parametros: construirParametrosPayload(etapa, qualidadeVals, {}),
      fotos_ciclo: construirFotosEtapaPayload(etapa, fotos),
    });
  };
  return (
    <ModalShell
      titulo={`Ciclo ${etapa._cicloNum}/${etapa._totalCiclos} · ${carrinho.lote_fabricacao}`}
      onCancel={onCancel}
      onConfirm={confirmar}
      confirmLabel="Tirar foto e concluir ciclo"
    >
      <p className="text-sm text-gray-500">
        Tempo do ciclo:{' '}
        {formatTempoEstimado(
          etapa.ciclos?.[(etapa._cicloNum || 1) - 1]?.tempo_estimado,
          etapa.ciclos?.[(etapa._cicloNum || 1) - 1]?.tempo_unidade
        )}
        . Registre os parâmetros ao final deste ciclo.
      </p>
      <ParametrosEtapaForm
        etapa={etapa}
        qualidadeVals={qualidadeVals}
        setQualidadeVals={setQualidadeVals}
        producaoVals={{}}
        setProducaoVals={() => {}}
      />
      <FotosEtapaCapture
        etapa={etapa}
        valores={fotos}
        setValores={setFotos}
        empresaId={empresaId}
        setorId={setorId}
        titulo="Fotos do ciclo"
      />
      {aviso && <p className="text-sm text-red-600">{aviso}</p>}
    </ModalShell>
  );
}

function PerdaModal({ carrinho, onCancel, onConfirm }) {
  const [motivo, setMotivo] = useState('');
  return (
    <ModalShell
      titulo={`Perda — ${carrinho.lote_fabricacao}`}
      onCancel={onCancel}
      onConfirm={() => onConfirm(motivo)}
      confirmLabel="Tirar foto e confirmar perda"
      disabled={motivo.trim().length < 3}
    >
      <p className="text-sm text-red-600">
        Marca o carrinho inteiro como perda (e todos os carrinhos gerados a partir dele).
      </p>
      <textarea
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        placeholder="Motivo da perda"
        rows={3}
        className="w-full rounded-lg border px-3 py-2 text-sm"
      />
    </ModalShell>
  );
}

function NovaCargaModal({ disponiveis, fornos, onCancel, onConfirm }) {
  const [equip, setEquip] = useState(fornos[0]?.id || '');
  const [sel, setSel] = useState([]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <ModalShell
      titulo="Pôr carrinhos no equipamento"
      onCancel={onCancel}
      onConfirm={() => onConfirm({ equipamento_id: equip, carrinho_ids: sel })}
      confirmLabel="Tirar foto e iniciar processamento"
      disabled={!equip || sel.length === 0}
    >
      <p className="text-sm text-gray-500">
        Só aparecem os carrinhos que chegaram a este setor pelo fluxo planejado. Escolha o equipamento e quais
        carrinhos vão nele (você pode dividir em equipamentos diferentes).
      </p>
      <label className="block text-sm font-medium text-gray-700">Equipamento</label>
      <select value={equip} onChange={(e) => setEquip(e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm">
        <option value="">Selecione…</option>
        {fornos.map((f) => (
          <option key={f.id} value={f.id}>
            {f.apelido || f.nome}
          </option>
        ))}
      </select>
      <div className="mt-2 space-y-1">
        <span className="text-sm font-medium text-gray-700">Carrinhos para este equipamento</span>
        {disponiveis.length === 0 && <p className="text-sm text-gray-400">Nenhum carrinho disponível.</p>}
        {disponiveis.map((c) => (
          <label key={c.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
            <input type="checkbox" checked={sel.includes(c.id)} onChange={() => toggle(c.id)} />
            <span className="font-medium">{c.lote_fabricacao}</span>
            <span className="text-gray-400">{c.lote_carrinho}</span>
          </label>
        ))}
      </div>
    </ModalShell>
  );
}

function FinalizarCargaModal({ carga, carrinhos, empresaId, setorId, onCancel, onConfirm }) {
  const entrada = useMemo(
    () => (carga.carrinho_ids_entrada || []).map((id) => carrinhos.find((c) => c.id === id)).filter(Boolean),
    [carga, carrinhos]
  );
  const etapa = useMemo(() => etapaDoCarrinho(entrada[0] || {}), [entrada]);
  const card = normCard(etapa.cardinalidade);
  const permiteVarios = card.endsWith('-n') || card.endsWith('-m');

  const lotesUnicos = useMemo(
    () => [...new Set(entrada.map((c) => c.lote_fabricacao))].filter(Boolean),
    [entrada]
  );

  // Inicialização: n-1 = 1 saída com todos; n-m = uma saída por lote (preserva rastreabilidade por padrão).
  const init = useMemo(() => {
    if (!permiteVarios) {
      return { num: 1, assign: Object.fromEntries(entrada.map((c) => [c.id, 0])) };
    }
    const idxByLote = Object.fromEntries(lotesUnicos.map((l, i) => [l, i]));
    return {
      num: lotesUnicos.length || 1,
      assign: Object.fromEntries(entrada.map((c) => [c.id, idxByLote[c.lote_fabricacao] ?? 0])),
    };
  }, [entrada, permiteVarios, lotesUnicos]);

  const [num, setNum] = useState(init.num);
  const [assign, setAssign] = useState(init.assign);
  const [quants, setQuants] = useState({});
  const [qualidadeVals, setQualidadeVals] = useState({});
  const [producaoVals, setProducaoVals] = useState({});
  const [fotos, setFotos] = useState({});
  const [pesoFinal, setPesoFinal] = useState('');
  const [embalagensSel, setEmbalagensSel] = useState([]);
  const [aviso, setAviso] = useState('');

  const cartsDoBucket = (b) => entrada.filter((c) => Number(assign[c.id]) === b);
  const lotesDoBucket = (b) => [...new Set(cartsDoBucket(b).map((c) => c.lote_fabricacao))].filter(Boolean);
  const somaBucket = (b) => cartsDoBucket(b).reduce((s, c) => s + Number(c.quantidade || 0), 0);
  const algumMisto = Array.from({ length: num }).some((_, b) => lotesDoBucket(b).length > 1);

  const confirmar = () => {
    const faltam = obrigatoriosFaltando(etapa, qualidadeVals, pesoFinal);
    if (faltam.length) {
      setAviso(`Preencha os parâmetros obrigatórios: ${faltam.join(', ')}`);
      return;
    }
    const fotosFaltam = fotosEtapaFaltando(etapa, fotos);
    if (fotosFaltam.length) {
      setAviso(`Envie as fotos da etapa: ${fotosFaltam.join(', ')}`);
      return;
    }
    if ((etapa?.embalagens_permitidas || []).length > 0 && embalagensSel.length === 0) {
      setAviso('Selecione ao menos uma embalagem utilizada.');
      return;
    }
    const saidas = [];
    for (let b = 0; b < num; b += 1) {
      const cs = cartsDoBucket(b);
      if (cs.length === 0) continue;
      const q = quants[b] != null && quants[b] !== '' ? quants[b] : somaBucket(b);
      saidas.push({ quantidade: q, carrinhos_origem_ids: cs.map((c) => c.id) });
    }
    if (saidas.length === 0) {
      setAviso('Defina ao menos um carrinho de saída com carrinhos de origem.');
      return;
    }
    onConfirm({
      saidas,
      parametros: construirParametrosPayload(etapa, qualidadeVals, producaoVals),
      fotos_etapa: construirFotosEtapaPayload(etapa, fotos),
      peso_final: etapa?.peso_final_ativo ? parsePesoFinal(pesoFinal) : null,
      embalagens_utilizadas: embalagensSel,
    });
  };

  return (
    <ModalShell titulo="Finalizar processamento" onCancel={onCancel} onConfirm={confirmar} confirmLabel="Tirar foto e finalizar">
      <p className="text-sm text-gray-500">
        {permiteVarios
          ? 'Você pode gerar vários carrinhos de saída. Defina em qual saída cada carrinho de entrada vai.'
          : 'Esta etapa gera um único carrinho de saída (N → 1).'}
      </p>

      {algumMisto && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          ⚠️ Há lotes diferentes no mesmo carrinho de saída. A mistura de lotes distintos afeta a rastreabilidade
          dos produtos — o carrinho será marcado como “misto”.
        </div>
      )}

      {/* Carrinhos de entrada (atribuição à saída quando há várias) */}
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-700">Carrinhos no equipamento</p>
        {entrada.map((c) => (
          <div key={c.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
            <span className="font-medium">{c.lote_fabricacao}</span>
            <span className="text-gray-400">{c.lote_carrinho}</span>
            <span className="text-gray-400">· {formatQuantidadeComUnidade(c.quantidade, c.unidade)}</span>
            {permiteVarios && num > 1 && (
              <select
                value={Number(assign[c.id] || 0)}
                onChange={(e) => setAssign((a) => ({ ...a, [c.id]: Number(e.target.value) }))}
                className="ml-auto rounded border px-2 py-1 text-xs"
              >
                {Array.from({ length: num }).map((_, b) => (
                  <option key={b} value={b}>
                    Saída {b + 1}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))}
        {permiteVarios && (
          <button onClick={() => setNum((n) => n + 1)} className="flex items-center gap-1 text-sm text-emerald-700">
            <Plus className="h-4 w-4" /> Adicionar carrinho de saída
          </button>
        )}
      </div>

      {/* Quantidades por saída */}
      <div className="space-y-2">
        {Array.from({ length: num }).map((_, b) => {
          const cs = cartsDoBucket(b);
          if (cs.length === 0 && num > 1) return null;
          const lts = lotesDoBucket(b);
          return (
            <div key={b} className="rounded-lg border px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-900">Saída {b + 1}</span>
                <span className="text-xs text-gray-400">
                  {cs.length} carrinho(s){lts.length > 1 ? ' · lotes ' + lts.join(', ') : lts[0] ? ' · ' + lts[0] : ''}
                </span>
              </div>
              <input
                type="number"
                value={quants[b] ?? ''}
                onChange={(e) => setQuants((q) => ({ ...q, [b]: e.target.value }))}
                placeholder={`Qtd saída (sugestão: ${somaBucket(b)})`}
                className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
              />
            </div>
          );
        })}
      </div>

      <ParametrosEtapaForm
        etapa={etapa}
        qualidadeVals={qualidadeVals}
        setQualidadeVals={setQualidadeVals}
        producaoVals={producaoVals}
        setProducaoVals={setProducaoVals}
      />
      <PesoFinalEtapaField
        etapa={etapa}
        valor={pesoFinal}
        setValor={setPesoFinal}
        unidade={entrada[0]?.unidade}
      />
      <EmbalagensEtapaField etapa={etapa} selecionadas={embalagensSel} setSelecionadas={setEmbalagensSel} />
      <FotosEtapaCapture etapa={etapa} valores={fotos} setValores={setFotos} empresaId={empresaId} setorId={setorId} />
      {aviso && <p className="text-sm text-red-600">{aviso}</p>}
    </ModalShell>
  );
}

export default KioskSetorPage;
